import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import { showMessage } from 'app/store/fuse/messageSlice';
import i18n from 'i18n';
import { selectUserFranchise } from '../../franchises/ducks/franchises.selector';
import { getFranchiseCrmFieldsApi, getFranchiseCrmFieldValuesApi, saveCrmFieldsApi } from '../api';
import { ClientCrmField } from '../types';
import { parseField } from '../utils';
import { crmFieldsSelector } from './crm.selector';

export const fetchCrmFormThunk = createAsyncThunk(
  'crm/fetchCrmFormThunk',
  async (params: { includeArchived?: boolean }, { dispatch, rejectWithValue, getState }) => {
    const includeArchived = params.includeArchived ?? true;

    try {
      const state = getState() as RootState;
      const franchise = selectUserFranchise(state);

      const { data } = await getFranchiseCrmFieldsApi(franchise.id, includeArchived);
      const fields = (data.fields || []).map(field => ({
        ...field,
        isValid: true,
        localId: field.id,
      })) as ClientCrmField[];

      return {
        ...data,
        fields,
      };
    } catch (error: any) {
      dispatch(
        showMessage({
          message: i18n.t('pages.crm.error.fetchCrmFieldError'),
          variant: 'error',
        }),
      );
      return rejectWithValue(error?.response?.status ?? 'UNKNOWN_ERROR');
    }
  },
);

export const fetchCrmFieldValuesThunk = createAsyncThunk(
  'crm/fetchCrmFieldValuesThunk',
  async (fieldId: string, { dispatch, rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;
      const franchise = selectUserFranchise(state);
      const { data } = await getFranchiseCrmFieldValuesApi(franchise.id, fieldId);

      return data;
    } catch (error: any) {
      dispatch(showMessage({ message: i18n.t('pages.crm.error.fetchCrmFieldValuesError'), variant: 'error' }));
      return rejectWithValue(error?.response?.status ?? 'UNKNOWN_ERROR');
    }
  },
);

export const saveCrmFieldsThunk = createAsyncThunk(
  'crm/saveCrmFieldsThunk',
  async (_, { dispatch, rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;

      const franchise = selectUserFranchise(state);
      const fields = crmFieldsSelector(state);
      const crmFieldsList = fields.map(field => parseField(field));

      const crm = {
        settings: state.crm.crmSettings,
        fields: crmFieldsList,
      };
      const { data } = await saveCrmFieldsApi(franchise.id, crm);

      dispatch(showMessage({ message: i18n.t('pages.crm.success'), variant: 'success' }));

      const crmFields = [
        ...(data.fields?.map(field => ({ ...field, isValid: true, localId: field.id }) as ClientCrmField) || []),
      ];

      return crmFields;
    } catch (error: any) {
      dispatch(showMessage({ message: i18n.t('pages.crm.error.saveFormError'), variant: 'error' }));
      return rejectWithValue(error?.response?.status ?? 'UNKNOWN_ERROR');
    }
  },
);
